/*

  e y e k a n d y  a r  l a u n c h
  EyekandyARLaunch

  :description:
  The abstract class for the AR Launch package.

*/

//
//  :code:
import { ENVIRONMENT, VERSION } from "../settings";
import {
  KNOWN_PLATFORMS, KNOWN_SERVICES, MESSAGE_GROUP_DEFAULT, PLATFORM_ANDROID, PLATFORM_IOS, PLATFORM_UNSET,
  KNOWN_ENVIRONMENTS
} from "../statics";
import { fromBase64, safeAddToDOM, toBase64 } from "../utils";

export default class EyekandyARLaunch {

  constructor(options) {
    if (!options) { options = {} }
    //
    //  :options:
    //  At our top-level we can accept parameters through the options.
    this.environment = options.environment || ENVIRONMENT
    this.platform = options.platform || PLATFORM_UNSET
    this.throws = typeof options.throws !== "undefined" ? options.throws : true

    //
    //  :core:
    this.version = VERSION
    this.group = MESSAGE_GROUP_DEFAULT
  }


  //
  //  :api:
  //  As an abstract class we have an abstract API of private functions destined to be called by child classes.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  /**
   * Our exposed function that can launch into AR with an external call.
   * @param {String} platform The destination platform to launch into AR for.
   * @param {String} service The service to launch into AR for.
   * @param {String} arid The ARID to use when launching into AR.
   * @param {String} environment The environment to use when launching into AR.
   * @param {Object} [options] A set of options to send when rending the AR URL.
   * @returns {Boolean} True if we clicked, False if we can't click from here, null if no URL given. 
   */
  launch(service, arid, environment, options, returnURL) {
    try {
      //
      //  Launch via our abstract function.
      this._validateEnvironmentAndARIDAndService(environment, arid, service)
      const launched = this._launch(this.platform, service, arid, environment, options, returnURL)
      //
      //  All done, we launched into AR (if it was supported) or are returning the AR URL itself.
      return launched
    } catch (err) {
      //console.error(err, err.stack)
      //
      //  If we falied to launch into AR, always raise a clear error.
      this._throw(this.group, `we failed to launch into AR for: '${this.platform}'`, err)
      /* istanbul ignore next */
      return false
    }
  }

  render3DFWUrl(arid, environment) {
    //
    //  Validate the inputs are valid before continuing.
    this._validate("environment", environment)
    //
    //  Ok, render the URL for this model.
    const threeDFWURL = `https://eyekandy-player.pointandplace.com/raw?arid=${arid}`
    //
    //  Return the URL to the caller.
    return threeDFWURL
  }


  //
  //  :getters:
  //  Functions to get values from our self.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  getEnvironment() { return this.environment }
  getPlatform() { return this.platform }


  //
  //  :setters:
  //  Functions to set values to our self.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  setEnvironment(environment) { this.environment = environment }
  setPlatform(platform) { this.platform = platform }


  //
  //  :internal:
  //  Functions internal to the class, enums etc.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  _getARLaunchOptions(service) { return {} }

  _validateEnvironment() { this._validate("environment") }
  _validatePlatform() { this._validate("platform") }

  _renderErrorName(name) { return `${name}-ERROR` }

  _throw(group, message, error) {
    if (!error) { error = {} }
    //
    //  Define the and write the message into the logs.
    const completeMessage = `[${group.toUpperCase()}] ${message}`
    //console.error(completeMessage, error.message || "", error.stack || "")
    //
    //  Throw the error we were given for the clean traceback, but only if set to throw.
    if (!this.throws) { return false }
    throw new Error(error)
  }

  _validateEnvironmentAndARID(environment, arid) {
    this._validateARID(arid)
    this._validate("environment", environment)
  }

  _validateEnvironmentAndARIDAndService(environment, arid, service) {
    this._validateARID(arid)
    this._validate("environment", environment)
    this._validate("service", service)
  }

  _validateARID(arid) {
    if (typeof arid === "string" && arid.length > 0) { return true }
    //
    //  Else, throw an error.
    this._throw(
      this.group, `error, invalid ARID '${arid}'`,
      new Error(`${this._renderErrorName(`ARID-NOT-VALID`)}`)
    )
    return false
  }

  _validate(type, value) {
    //
    //  Define our known values as keys we can lookup.
    const knowns = {
      environment: KNOWN_ENVIRONMENTS,
      platform: KNOWN_PLATFORMS,
      service: KNOWN_SERVICES,
    }
    //
    //  If it's valid, return true.
    const valueToLookUp = value ? value : this[type]
    if (knowns[type] && knowns[type].includes(valueToLookUp)) { return true }
    //
    //  Else, throw an error.
    this._throw(
      this.group, knowns[type] ? `'${type}' is an invalid value, must be in '${knowns[type].join(", ")}'` : `unknown type given '${type}'`,
      new Error(`${this._renderErrorName(`${type.toUpperCase()}-NOT-VALID`)}`)
    )
    return false
  }


  //
  //  :internal-api:
  //  Should not be used be external callers.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  /**
   * By default we'll use space-stripped base64 encoding.
   * @param {String} value The value to encode/decode.
   * @returns An encoded/decoded value.
   */
  _encode(value) { return toBase64(value).replace(/=/ig, "") }
  _decode(value) { return fromBase64(value) }

  /**
   * Our internal function that can launch into AR.
   * @param {String} platform The destination platform to launch into AR for.
   * @param {String} service The service to launch into AR for.
   * @param {String} arid The ARID to use when launching into AR.
   * @param {String} environment The environment to use when launching into AR.
   * @param {Object} [options] A set of options to send when rending the AR URL.
   * @returns {Boolean} True if we clicked, False if we can't click from here, null if no URL given. 
   * @returns {Boolean} If flagged we will return the URL itself before clicking.
   */
  _launch(platform, service, arid, environment, options, returnURL) {
    //
    //  :step 0:
    //  Take the options and override our defaults with them.
    if (options) {
      const defaults = this._getARLaunchOptions(service)
      options = { ...defaults, ...options }
    }
    //
    //  If there are no options, use the default ones.
    if (!options) { options = this._getARLaunchOptions(service) }

    //
    //  :step 1:
    //  The functionality we need may not be available in our instance class, check first.
    const RENDER_AR_LAUNCH_URL_FUNCTION_NAME = "renderLaunchARURL"
    if (typeof this[RENDER_AR_LAUNCH_URL_FUNCTION_NAME] !== "function") { return false }

    //
    //  :step 2:
    //  Different platforms require different URLs, handle that based on the input for platform.
    //  Our instance needs to be a class that has this function.
    let arURL = null
    //
    //  Note, these calls are identical at the time of writing, but may not always be.
    if (platform === PLATFORM_ANDROID) {
      arURL = this[RENDER_AR_LAUNCH_URL_FUNCTION_NAME](service, arid, environment, options)
    }
    if (platform === PLATFORM_IOS) {
      arURL = this[RENDER_AR_LAUNCH_URL_FUNCTION_NAME](service, arid, environment, options)
    }
    //
    //  Throw on error here just in case the user has used this function directly.
    if (!arURL) {
      /* istanbul ignore next */
      this._throw(
        this.group, `failed to launch into AR for: '${this.platform}' '${service}' '${arid}'`,
        new Error(this._renderErrorName("NO-AR-URL"))
      )
    }

    //
    //  :step 2a:
    //  We might be set to only return the URL.
    if (returnURL) { return arURL }

    //
    //  :step 3:
    //  If we failed to create our AR URL above, it would of raised, so here we can assume we have it, 
    //  just click it using a button we place into the DOM.
    //  Caution: iOS can bind listeners to the button, we need to add those to DOM.
    const clicked = this._click(arURL, options.callback, options.context)

    //
    //  :step 4:
    //  All done, return the result of our click (true if clicked, else false).
    return clicked
  }

  /**
   * Our internal function to "click" a URL.
   * @param {String} urlToClick The URL to "click" on.
   * @returns {Boolean} True if we clicked, False if we can't click from here, null if no URL given. 
   */
  _click(urlToClick, callbackFunction, callbackContext) {
    //
    //  :step 0:
    //  Make sure we have something to click / place our anchor.
    if (typeof document === "undefined") { return false }
    if (typeof urlToClick !== "string") { return null }
    if (urlToClick.length === 0) { return null }

    //
    //  :step 1a:
    //  For universal compatibility we really need to consider a mechanism that is consistent across different
    //  platforms, we'll use the DOM for now.
    const anchor = document.createElement('a')
    //
    //  :step 1b:
    //  For iOS we'll need an invisible image icon too, and potentially a callback function with optional context.
    if (urlToClick.includes(".usdz")) {
      anchor.setAttribute("rel", "ar")
      const image = document.createElement("img")
      image.style.display = "none"
      anchor.appendChild(image)
      //
      //  To detect taps on a banner in AR, we need to bind a function now.
      //  It will stay bound, and act on its own to trigger the given callback function.
      if (typeof callbackFunction === "function") {
        /* istanbul ignore next */
        if (!callbackContext) { callbackContext = this }
        /* istanbul ignore next */
        anchor.addEventListener("message", callbackFunction.bind(callbackContext), false);
      }
    }

    //
    //  :step 2:
    //  Now set the attribute via a top-level call.
    anchor.setAttribute("href", urlToClick)
    //
    //  @Louis: This is likely only required for the bound iOS listeners?
    safeAddToDOM(anchor)

    //
    //  :step 3:
    //  Click this anchor.
    //  Note, it's not in the DOM here, but that seems to be ok in 2023.
    anchor.click()

    //
    //  Return true as we clicked the button.
    return true
  }

  /*
  //
  //  @Louis: 2023-01-27 We are now dynamically passing this, the caller must take care on their side to check
  //          that it is an event of {data: "_apple_ar_quicklook_button_tapped"}
  //
  _tapInARCallback(event) {
    //
    //  This is the action when someone clicks on the banner inside iOS.
    if (event.data == "_apple_ar_quicklook_button_tapped") {}
  }
  */

}
